<template>
  <poppins-counter
    v-slot="slotProps"
    :factor="factor"
    :label="screenReaderOnlyLabel"
    :max="max"
    :min="min"
    :modelValue="localCount"
    @update:model-value="onClickUpdateCount"
  >
    <span class="text-grey-text-subdued superscript"> $ </span>
    <span class="font-medium -mr-1 text-m2">
      {{ slotProps.count }}
    </span>
    <span class="text-grey-text-subdued" aria-label="per hour"> /hr </span>
  </poppins-counter>
</template>

<script lang="ts">
import PoppinsCounter from '../counter/PoppinsCounter.vue';

/**
 * The Rate component is a specific type of Counter that we use for displaying money per hour.
 * We had to create it because Counter is not set up to display currency.
 * Rate overrides Counter's default label using a slot.
 * It will appear in Search filters for childcare profile search:
 * https://www.sittercity.com/availability/search?zipcode=60601&careType=babysitter when ch111815 is done.
 */
export default {
  name: 'PoppinsRate',
  components: {
    PoppinsCounter,
  },
  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },
  props: {
    /**
     * Increment and decrement only happens by whole dollar amounts.
     */
    factor: {
      type: Number,
      default: 1,
    },
    /**
     * Sitters cannot charge less than a dollar.
     * This property is unlikely to change.
     */
    min: {
      type: Number,
      default: 1,
    },
    /**
     * Currently, the maximum rate on production is $100/hr.
     * This may change or get an override in places other than search.
     */
    max: {
      type: Number,
      default: 100,
    },
    /**
     * Value gets passed from parent components, such as SearchForm
     */
    modelValue: {
      type: Number,
      required: false,
    },
  },
  data() {
    return {
      /**
       * Making local copy of value passed in from parent
       * so we don't have to mutate a prop
       */
      localCount: this.modelValue,
    };
  },
  computed: {
    /**
     * We have to have a separate label for screenreader because otherwise
     * it will read 'twelve dollars slash H. R.'
     */
    screenReaderOnlyLabel() {
      const dollarOrDollars = this.localCount === 1 ? 'dollar' : 'dollars';

      return `${this.localCount} ${dollarOrDollars} per hour`;
    },
  },
  watch: {
    /**
     * Updating localCount if parent updates value programmatically (e.g. a reset function)
     */
    modelValue(newVal) {
      this.localCount = newVal;
    },
  },
  methods: {
    /**
     * V-model does not work with slots so we imitate it
     * by emitting update:modelValue
     */
    onClickUpdateCount(newCount) {
      this.localCount = newCount;
      this.$emit('update:modelValue', this.localCount);
    },
  },
};
</script>

<style scoped>
.superscript {
  position: relative;
  font-size: 1rem;
  top: -0.5rem;
  right: -0.2rem;
}
</style>
